/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Main entry point for the i18n package. Exports the default i18n instance
 * and factory for creating custom instances with overrides.
 *
 * */

import { I18nFactory } from './factory/I18nFactory'
import enMX from './locales/en-mx'
import esMX from './locales/es-mx'
import type { Ti18nConfig, Ti18nLocale, Ti18nParams } from './types'

export type { Ti18nKey } from './locales/keys'
export type { Ti18nDictionary } from './locales/dictionary'
export type { Ti18nConfig, Ti18nLocale, Ti18nParams }

// Export classes and factory for custom usage
export { I18nFactory } from './factory/I18nFactory'
export { I18nService } from './services/I18nService'
export { TranslationProvider } from './providers/TranslationProvider'

// Export locale constants
export const locales: Ti18nLocale[] = ['en-mx', 'es-mx']

// Create default i18n instance
const defaultTranslations = {
  'en-mx': enMX,
  'es-mx': esMX,
}

const i18n = I18nFactory.createWithProvider(defaultTranslations, {
  defaultLocale: 'es-mx',
  missingKeyBehavior: 'placeholder',
})

// Export default instance methods
export const locale = (loc?: Ti18nLocale): Ti18nLocale => i18n.locale(loc)
export const getLocale = (): Ti18nLocale => i18n.getLocale()
export const t = (key: string, params?: Ti18nParams): string =>
  i18n.t(key, params)
export const exists = (key: unknown): boolean => i18n.exists(key)

// Export default instance
export default i18n

/**
 * Create a custom i18n instance with translation overrides
 * @param overrides - Partial translations to override defaults
 * @param config - Optional configuration
 * @returns Custom i18n instance
 */
export const createI18n = (
  overrides?: Partial<Record<Ti18nLocale, Record<string, string>>>,
  config?: Ti18nConfig
) => {
  if (!overrides) {
    return I18nFactory.createWithProvider(defaultTranslations, config)
  }
  return I18nFactory.createWithOverrides(defaultTranslations, overrides, config)
}
